import DOMPurify from 'dompurify';
import _ from 'lodash';
import { isPropElement } from './chart-settings/custom-chart-schema-hydrator.util';
export const generateVisualProps = (elements) => {
    const visualProps = {};
    elements.forEach(element => {
        if (element.type === 'customsort') {
            return;
        }
        if (element.type === 'section') {
            _.set(visualProps, element.key, generateVisualProps(element.children));
        }
        else {
            let defaultValue = element.defaultValue;
            if (typeof element.defaultValue === 'object' &&
                'value' in element.defaultValue) {
                defaultValue = element.defaultValue.value;
            }
            _.set(visualProps, element.key, defaultValue);
        }
    });
    return visualProps;
};
export const generateVisPropWithColumnSettings = (visualPropEditorDefinition) => {
    let currentVisualProps = {};
    if (!visualPropEditorDefinition)
        return currentVisualProps;
    const { elements, columnsVizPropDefinition } = visualPropEditorDefinition;
    if (elements && elements.length) {
        currentVisualProps = generateVisualProps(elements);
    }
    if (columnsVizPropDefinition && columnsVizPropDefinition.length) {
        currentVisualProps.columnVisualProps = {};
        columnsVizPropDefinition.forEach(item => {
            Object.keys(item.columnSettingsDefinition).forEach(colId => {
                const columnSettingsDefinition = item.columnSettingsDefinition[colId];
                const elements = columnSettingsDefinition.elements;
                const filteredElements = elements.filter(isPropElement);
                const filteredColumnSettingsDefinition = {
                    elements: filteredElements,
                };
                currentVisualProps.columnVisualProps[colId] = generateVisualProps(filteredColumnSettingsDefinition.elements);
            });
        });
    }
    return currentVisualProps;
};
export const validateInputField = (value, rules) => {
    if (!rules) {
        return '';
    }
    const { required, requiredError, minLength, minLengthError, regex, regexError, range, rangeError, } = rules;
    const trimmedValue = _.isString(value) ? value.trim() : String(value);
    if (required) {
        const isValueEmpty = _.isString(trimmedValue)
            ? trimmedValue === ''
            : value === undefined || value === null;
        if (isValueEmpty) {
            return requiredError;
        }
    }
    if (minLength !== undefined &&
        trimmedValue.length > 0 &&
        trimmedValue.length < minLength) {
        return minLengthError;
    }
    if (regex) {
        if (!required && trimmedValue === '') {
        }
        else {
            const pattern = new RegExp(regex);
            if (!pattern.test(trimmedValue)) {
                return regexError;
            }
        }
    }
    if (range) {
        if (!required && trimmedValue === '') {
        }
        else {
            const [min, max] = range.split('-').map(parseFloat);
            const numericValue = parseFloat(trimmedValue);
            if (_.isNaN(numericValue) ||
                numericValue < min ||
                numericValue > max) {
                return rangeError;
            }
        }
    }
    return '';
};
const isValidType = (value, element) => {
    switch (element.type) {
        case 'toggle':
        case 'checkbox':
            return typeof value === 'boolean';
        case 'dropdown':
        case 'radio':
            return (typeof value === 'string' ||
                (typeof value === 'object' && 'value' in value));
        case 'customsort':
            return true;
        default:
            return true;
    }
};
const hasValidData = (value, element) => {
    switch (element.type) {
        case 'dropdown':
        case 'radio':
            return element.values.some(v => {
                if (typeof v === 'string') {
                    return v === value;
                }
                return v.value === value;
            });
        case 'text':
        case 'number':
            return _.isEmpty(validateInputField(value, element.inputValidation));
        case 'checkbox':
        case 'toggle':
            return _.isBoolean(value);
        default:
            return true;
    }
};
export const validateVisualPropJSON = (json, elements, elementKey = '') => {
    const errors = [];
    elements.forEach(element => {
        if (!_.has(json, element.key)) {
            return;
        }
        if (element.type === 'section') {
            const sectionErrors = validateVisualPropJSON(json[element.key], element.children, element.key);
            if (sectionErrors.length > 0) {
                errors.push(...sectionErrors);
            }
        }
        else {
            const value = _.get(json, element.key);
            if (!isValidType(value, element)) {
                errors.push(`Type mismatch for key: ${elementKey}${elementKey ? '.' : ''}${element.key}`);
                return;
            }
            if (!hasValidData(value, element)) {
                errors.push(`Invalid value for key: ${elementKey}${elementKey ? '.' : ''}${element.key}`);
            }
        }
    });
    return errors;
};
const sanitizeElementsArray = (currentVisualProps, elements, globalPersistedVisualPropKeys) => {
    const visualProps = {};
    elements.forEach(element => {
        if (element.type === 'customsort') {
            return;
        }
        const currentElementsVisProp = (element) => {
            if (element.type === 'customsort') {
                return null;
            }
            if (element.type === 'section') {
                return sanitizeElementsArray(currentVisualProps?.[element.key], element.children, globalPersistedVisualPropKeys);
            }
            let defaultValue = element.defaultValue;
            if (typeof element.defaultValue === 'object' &&
                'value' in element.defaultValue) {
                defaultValue = element.defaultValue.value;
            }
            return currentVisualProps && currentVisualProps.hasOwnProperty(element.key)
                ? currentVisualProps[element.key]
                : defaultValue;
        };
        _.set(visualProps, element.key, currentElementsVisProp(element));
    });
    const currentVisualPropsWithPreservedState = currentVisualProps?.clientState
        ? {
            ...visualProps,
            clientState: DOMPurify.sanitize(currentVisualProps.clientState),
        }
        : visualProps;
    if (!globalPersistedVisualPropKeys ||
        _.isEmpty(globalPersistedVisualPropKeys)) {
        return currentVisualPropsWithPreservedState;
    }
    globalPersistedVisualPropKeys.forEach(value => {
        if (_.get(currentVisualProps, value)) {
            _.set(currentVisualPropsWithPreservedState, value, DOMPurify.sanitize(_.get(currentVisualProps, value)));
        }
    });
    return currentVisualPropsWithPreservedState;
};
export const sanitizeCustomVisualProps = (customVisualProps, visualPropEditorDefinition, globalPersistedVisualPropKeys) => {
    let currentVisualProps = {};
    const { elements, columnsVizPropDefinition } = visualPropEditorDefinition;
    if (elements && elements.length) {
        currentVisualProps = sanitizeElementsArray(customVisualProps, elements, globalPersistedVisualPropKeys);
    }
    if (columnsVizPropDefinition && columnsVizPropDefinition.length) {
        currentVisualProps.columnVisualProps = {};
        columnsVizPropDefinition.forEach(item => {
            Object.keys(item.columnSettingsDefinition).forEach(colId => {
                currentVisualProps.columnVisualProps[colId] = sanitizeElementsArray(customVisualProps?.columnVisualProps?.[colId] ?? {}, item?.columnSettingsDefinition?.[colId]
                    ?.elements, globalPersistedVisualPropKeys);
            });
        });
    }
    let currentVisualPropsWithPreservedState = customVisualProps?.clientState
        ? {
            ...currentVisualProps,
            clientState: DOMPurify.sanitize(customVisualProps.clientState),
        }
        : currentVisualProps;
    if (!globalPersistedVisualPropKeys ||
        _.isEmpty(globalPersistedVisualPropKeys)) {
        return currentVisualPropsWithPreservedState;
    }
    globalPersistedVisualPropKeys.forEach(value => {
        currentVisualPropsWithPreservedState = {
            ...currentVisualPropsWithPreservedState,
            [value]: DOMPurify.sanitize(_.get(customVisualProps, value) ?? ''),
        };
    });
    return currentVisualPropsWithPreservedState;
};
export const sdkToTSTranslationKeyMap = {
    SHOW_ALL_LABELS: 'charts.chartEditor.SHOW_ALL_LABELS',
    TOO_MANY_LABELS: 'charts.chartEditor.TOO_MANY_LABELS',
    MAP_TILE_LABEL: 'charts.chartEditor.MAP_TILE_LABEL',
    ENABLE_MARKERS: 'charts.chartEditor.ENABLE_MARKERS',
    SHOW_REGRESSION_LINE: 'charts.chartEditor.SHOW_REGRESSION_LINE',
    X_AXIS_GRID_LINE: 'charts.chartEditor.X_AXIS_GRID_LINE',
    Y_AXIS_GRID_LINE: 'charts.chartEditor.Y_AXIS_GRID_LINE',
    MAX_DATA_POINTS: 'charts.chartEditor.MAX_DATA_POINTS',
    highCardinalityBatchSizeDisabled: 'charts.chartEditor.highCardinalityBatchSizeDisabled',
    highCardinalityBatchSizeLimit: 'charts.chartEditor.highCardinalityBatchSizeLimit',
    CHART_CUSTOMIZE: 'charts.chartEditor.CHART_CUSTOMIZE',
    'chartConfigurator.SELECT_AN_AREA': 'chart.chartEditor.chartConfigurator.SELECT_AN_AREA',
    'chartConfigurator.RESET_ZOOM': 'chart.chartEditor.chartConfigurator.RESET_ZOOM',
    EDIT_TOOLTIP: 'charts.chartEditor.EDIT_TOOLTIP',
    DONT_SHOW: 'charts.chartEditor.DONT_SHOW',
    SHOW_GAP: 'charts.chartEditor.SHOW_GAP',
    SHOW_AS_ZERO: 'charts.chartEditor.SHOW_AS_ZERO',
    HANDLE_MISSING_VALUES: 'charts.chartEditor.HANDLE_MISSING_VALUES',
    SHOW_NULL_AS_ZERO: 'charts.chartEditor.SHOW_NULL_AS_ZERO',
    EXCLUDE_NULL_VALUES: 'charts.chartEditor.EXCLUDE_NULL_VALUES',
    COLUMN_CUSTOMIZE: 'charts.chartEditor.COLUMN_CUSTOMIZE',
    SHOW_TOTAL_LABELS: 'charts.chartEditor.SHOW_TOTAL_LABELS',
    SHOW_DETAILED_LABELS: 'charts.chartEditor.SHOW_DETAILED_LABELS',
    SHOW_DATA_LABELS: 'charts.chartEditor.SHOW_DATA_LABELS',
    RIGHT_LEGEND: 'charts.chartEditor.RIGHT_LEGEND',
    LEFT_LEGEND: 'charts.chartEditor.LEFT_LEGEND',
    TOP_LEGEND: 'charts.chartEditor.TOP_LEGEND',
    BOTTOM_LEGEND: 'charts.chartEditor.BOTTOM_LEGEND',
    SHOW_AXIS_AS_PERCENT: 'charts.chartEditor.SHOW_AXIS_AS_PERCENT',
};
//# sourceMappingURL=form-builder.util.js.map